import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

// Top-level log to confirm module loads
console.log('🎯 VoiceClient.js module loaded');

const API_URL = process.env.REACT_APP_BACKEND_URL;
console.log('🔗 VoiceClient API_URL:', API_URL);

const VoiceClient = () => {
  console.log('🎤 VoiceClient component rendering');
  const { getAuthHeaders, user } = useAuth();
  console.log('🔐 VoiceClient user:', user?.email, 'authenticated:', !!user);
  
  const [device, setDevice] = useState(null);
  const [connection, setConnection] = useState(null);
  const [status, setStatus] = useState('disconnected'); // disconnected, ready, ringing, on-call
  const [incomingCall, setIncomingCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState(null);
  const timerRef = useRef(null);

  // Initialize Twilio Device
  const initializeDevice = useCallback(async () => {
    console.log('📞 initializeDevice called');
    try {
      console.log('🌐 Fetching token from:', `${API_URL}/api/communications/voice/token`);
      const headers = getAuthHeaders();
      console.log('📋 Request headers:', headers);
      
      const response = await fetch(`${API_URL}/api/communications/voice/token`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      console.log('📨 Token response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Token fetch failed:', response.status, errorText);
        throw new Error('Failed to get voice token');
      }
      
      const data = await response.json();
      
      console.log('✅ Got voice token, identity:', data.identity);
      console.log('🎟️ Token length:', data.token?.length);
      
      // Create new Device with edge configuration
      const newDevice = new Device(data.token, {
        codecPreferences: ['opus', 'pcmu'],
        enableRingingState: true,
        logLevel: 'debug',
        edge: ['ashburn', 'dublin', 'singapore']  // Use multiple edge locations
      });
      
      // Set up event handlers
      newDevice.on('registered', () => {
        console.log('✅ Twilio Device registered and ready to receive calls');
        console.log('Device state:', newDevice.state);
        setStatus('ready');
      });
      
      newDevice.on('unregistered', () => {
        console.log('❌ Twilio Device unregistered');
        setStatus('disconnected');
      });
      
      newDevice.on('registering', () => {
        console.log('🔄 Twilio Device registering...');
      });
      
      newDevice.on('error', (error) => {
        console.error('Twilio Device error:', error);
        toast.error(`Voice error: ${error.message}`);
        setStatus('disconnected');
      });
      
      newDevice.on('tokenWillExpire', () => {
        console.log('⚠️ Token will expire soon, refreshing...');
        initializeDevice();
      });
      
      newDevice.on('incoming', (call) => {
        console.log('📞📞📞 INCOMING CALL! 📞📞📞');
        console.log('📞 Call from:', call.parameters.From);
        console.log('📞 Call SID:', call.parameters.CallSid);
        console.log('📞 Call parameters:', JSON.stringify(call.parameters));
        
        setIncomingCall(call);
        setCallerInfo({
          from: call.parameters.From,
          callSid: call.parameters.CallSid
        });
        setStatus('ringing');
        
        // Play ringtone notification
        toast.info('📞 Incoming call...', {
          duration: 30000,
          id: 'incoming-call'
        });
        
        // Try to play a sound to alert user (requires user gesture first)
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQUAUqbbqoM8CANK0NqsYxscC0nP1alXHRQQRs/UolUcFA1J0tWiVB4UDEXQ1aRWHxQMRs/UpVYhEgxG0dWkVyITDUXQ1aNXIxIMRc/Vo1ciEg1G0NWjWSMSDUXP1aRZIxINRc/UpFojEg1Fz9SjWiMSDUXQ1KNaIxINRc/Uo1sjEg1Fz9SjWyMSDUXQ1KNaIxINRc/Vo1ojEg1F0NWjWyMSDUXQ1aNbIxINRdDUo1sjEg1F0NSjWyMSDUXQ1KNb');
          audio.play().catch(e => console.log('Audio play blocked:', e));
        } catch (e) {
          console.log('Audio notification error:', e);
        }
        
        // Set up call event handlers
        call.on('accept', () => {
          console.log('Call accepted');
          setConnection(call);
          setStatus('on-call');
          setIncomingCall(null);
          startTimer();
          toast.dismiss('incoming-call');
        });
        
        call.on('disconnect', () => {
          console.log('Call disconnected');
          handleCallEnd();
        });
        
        call.on('cancel', () => {
          console.log('Call cancelled');
          handleCallEnd();
          toast.dismiss('incoming-call');
        });
        
        call.on('reject', () => {
          console.log('Call rejected');
          handleCallEnd();
        });
      });
      
      // Register the device
      await newDevice.register();
      setDevice(newDevice);
      
      console.log('✅ Twilio Device setup complete');
      
    } catch (error) {
      console.error('Failed to initialize voice client:', error);
      toast.error('Failed to initialize voice client');
    }
  }, [getAuthHeaders]);

  // Warm up AudioContext on first user interaction
  useEffect(() => {
    const warmUpAudio = () => {
      try {
        // Create and immediately close an AudioContext to "warm it up"
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        ctx.resume().then(() => {
          console.log('🔊 AudioContext warmed up');
        });
      } catch (e) {
        console.log('AudioContext warmup error:', e);
      }
      // Remove listener after first interaction
      document.removeEventListener('click', warmUpAudio);
      document.removeEventListener('keydown', warmUpAudio);
    };
    
    document.addEventListener('click', warmUpAudio);
    document.addEventListener('keydown', warmUpAudio);
    
    return () => {
      document.removeEventListener('click', warmUpAudio);
      document.removeEventListener('keydown', warmUpAudio);
    };
  }, []);

  // Initialize on mount
  useEffect(() => {
    console.log('🔄 VoiceClient useEffect triggered, user:', !!user);
    if (user) {
      console.log('👤 User exists, calling initializeDevice');
      initializeDevice();
    } else {
      console.log('⚠️ No user yet, skipping initialization');
    }
    
    return () => {
      console.log('🧹 VoiceClient cleanup');
      if (device) {
        device.destroy();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [user]);

  // Refresh token periodically (every 50 minutes)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (status === 'ready' && device) {
        initializeDevice();
      }
    }, 50 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [status, device, initializeDevice]);

  const startTimer = () => {
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const handleCallEnd = () => {
    setConnection(null);
    setIncomingCall(null);
    setCallerInfo(null);
    setStatus('ready');
    setIsMuted(false);
    setCallDuration(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const answerCall = () => {
    if (incomingCall) {
      incomingCall.accept();
    }
  };

  const rejectCall = () => {
    if (incomingCall) {
      incomingCall.reject();
      handleCallEnd();
      toast.dismiss('incoming-call');
    }
  };

  const hangUp = () => {
    if (connection) {
      connection.disconnect();
    }
    handleCallEnd();
  };

  const toggleMute = () => {
    if (connection) {
      if (isMuted) {
        connection.mute(false);
      } else {
        connection.mute(true);
      }
      setIsMuted(!isMuted);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown';
    // Remove 'client:' prefix if present
    if (phone.startsWith('client:')) {
      return 'Internal Call';
    }
    // Format phone number
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Incoming call UI
  if (status === 'ringing' && incomingCall) {
    return (
      <Card className="fixed bottom-4 right-4 w-80 z-50 shadow-2xl border-2 border-green-500 animate-pulse">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Badge className="bg-green-500">Incoming Call</Badge>
            <PhoneIncoming className="h-6 w-6 text-green-500 animate-bounce" />
          </div>
          <div className="text-center mb-4">
            <p className="text-lg font-semibold">{formatPhoneNumber(callerInfo?.from)}</p>
            <p className="text-sm text-muted-foreground">Transferred from AI Assistant</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={rejectCall}
              variant="destructive"
              className="flex-1"
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={answerCall}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Phone className="h-4 w-4 mr-2" />
              Answer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // On call UI
  if (status === 'on-call' && connection) {
    return (
      <Card className="fixed bottom-4 right-4 w-80 z-50 shadow-2xl border-2 border-blue-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Badge className="bg-blue-500">On Call</Badge>
            <span className="text-lg font-mono">{formatDuration(callDuration)}</span>
          </div>
          <div className="text-center mb-4">
            <p className="text-lg font-semibold">{formatPhoneNumber(callerInfo?.from)}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={toggleMute}
              variant={isMuted ? "destructive" : "outline"}
              className="flex-1"
            >
              {isMuted ? <MicOff className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
              {isMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Button
              onClick={hangUp}
              variant="destructive"
              className="flex-1"
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              End
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Status indicator (minimal when ready)
  if (status === 'ready') {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Badge 
          className="bg-green-600 text-white px-3 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-green-700 transition-colors"
          onClick={() => {
            // Clicking warms up AudioContext and confirms readiness
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              ctx.resume();
              toast.success('Voice client ready to receive calls!');
              console.log('🔊 AudioContext activated by click');
            } catch (e) {
              console.log('AudioContext activation error:', e);
            }
          }}
        >
          <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
          Voice Ready
        </Badge>
      </div>
    );
  }

  // Disconnected state
  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Badge 
        variant="outline" 
        className="cursor-pointer hover:bg-gray-100 px-3 py-1.5"
        onClick={initializeDevice}
      >
        <Phone className="h-3 w-3 mr-2" />
        Connect Voice
      </Badge>
    </div>
  );
};

export default VoiceClient;
